import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { announcementService } from '@/lib/api/services/announcement'
import { eventsService } from '@/lib/api/services/events'
import { pressReleaseService } from '@/lib/api/services/press-release'
import { programmeService } from '@/lib/api/services/programme'
import { strategicPillarService } from '@/lib/api/services/strategic-pillar'
import type {
  AnnouncementFilterParams,
  PaginationParams,
  CreateAnnouncement,
  UpdateAnnouncement
} from '@/lib/api/types'

// Query keys for announcement management
export const announcementKeys = {
  all: ['announcements'] as const,
  announcements: () => [...announcementKeys.all, 'announcements'] as const,
  announcement: (id: string) => [...announcementKeys.announcements(), id] as const,
  announcementsPaginated: (params: PaginationParams, filters: AnnouncementFilterParams) =>
    [...announcementKeys.announcements(), 'paginated', params, filters] as const,
  contentCategories: () => [...announcementKeys.all, 'content-categories'] as const,
  highlighted: () => [...announcementKeys.announcements(), 'highlighted'] as const
}

/**
 * Get content categories
 */
export const useContentCategories = () => {
  return useQuery({
    queryKey: announcementKeys.contentCategories(),
    queryFn: () => announcementService.getContentCategories(),
    staleTime: 30 * 60 * 1000 // 30 minutes
  })
}

/**
 * Get announcements with pagination and filters
 */
export const useAnnouncements = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: AnnouncementFilterParams = {}
) => {
  return useQuery({
    queryKey: announcementKeys.announcementsPaginated(params, filters),
    queryFn: () => announcementService.getAnnouncements(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}

/**
 * Get highlighted announcement (current highlight)
 */
export const useHighlightedAnnouncement = () => {
  return useQuery({
    queryKey: announcementKeys.highlighted(),
    queryFn: () =>
      announcementService.getAnnouncements({ page: 1, size: 1 }, { highlighted: true }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    select: (data) => {
      if (data?.success && data?.data?.data && data.data.data.length > 0) {
        return data.data.data[0]
      }
      return null
    }
  })
}

/**
 * Get announcement by ID
 */
export const useAnnouncement = (announcementId: string) => {
  return useQuery({
    queryKey: announcementKeys.announcement(announcementId),
    queryFn: () => announcementService.getAnnouncementById(announcementId),
    enabled: !!announcementId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Get content by category for highlighting
 * Fetches published content with large page size
 */
export const useContentByCategory = (category: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['content', category, 'published'],
    queryFn: async () => {
      // Fetch published content with large page size (100 items)
      const params = { page: 1, size: 100 }
      
      switch (category) {
        case 'EVENT':
          // Events: Use getEvents with large page size (events don't have status filter like others)
          try {
            const response = await eventsService.getEvents(params)
            // Extract data array from paginated response
            if (response.success && response.data?.data) {
              return { success: true, data: response.data.data, message: response.message }
            }
            return response
          } catch (error) {
            console.error('Error fetching events:', error)
            return { success: false, message: 'Events fetch failed', data: [] }
          }
        case 'PRESS_RELEASE': {
          const prResponse = await pressReleaseService.getPressReleases(params, { status: 'PUBLISH' })
          // Extract data array from paginated response
          if (prResponse.success && prResponse.data?.data) {
            return { success: true, data: prResponse.data.data, message: prResponse.message }
          }
          return prResponse
        }
        case 'PROGRAMME': {
          const progResponse = await programmeService.getProgrammes(params, { status: 'PUBLISH' })
          // Extract data array from paginated response
          if (progResponse.success && progResponse.data?.data) {
            return { success: true, data: progResponse.data.data, message: progResponse.message }
          }
          return progResponse
        }
        case 'STRATEGIC_PILLAR': {
          const spResponse = await strategicPillarService.getStrategicPillars(params, { status: 'PUBLISH' })
          // Extract data array from paginated response
          if (spResponse.success && spResponse.data?.data) {
            return { success: true, data: spResponse.data.data, message: spResponse.message }
          }
          return spResponse
        }
        default:
          return { success: false, message: 'Invalid category', data: [] }
      }
    },
    enabled: enabled && !!category,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Create announcement
 */
export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAnnouncement) => announcementService.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.announcements() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.highlighted() })
    },
    onError: (error) => {
      console.error('Announcement creation failed:', error)
    }
  })
}

/**
 * Update announcement
 */
export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ announcementId, data }: { announcementId: string; data: Partial<UpdateAnnouncement> }) =>
      announcementService.updateAnnouncement(announcementId, data),
    onSuccess: (_response, { announcementId }) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.announcement(announcementId) })
      queryClient.invalidateQueries({ queryKey: announcementKeys.announcements() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.highlighted() })
    },
    onError: (error) => {
      console.error('Announcement update failed:', error)
    }
  })
}

/**
 * Delete announcement
 */
export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (announcementId: string) => announcementService.deleteAnnouncement(announcementId),
    onSuccess: (_response, announcementId) => {
      queryClient.removeQueries({ queryKey: announcementKeys.announcement(announcementId) })
      queryClient.invalidateQueries({ queryKey: announcementKeys.announcements() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.highlighted() })
    },
    onError: (error) => {
      console.error('Announcement deletion failed:', error)
    }
  })
}

/**
 * Highlight announcement
 */
export const useHighlightAnnouncement = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ announcementId, data }: { announcementId: string; data: { highlight: boolean } }) =>
      announcementService.highlightAnnouncement(announcementId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.highlighted() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.announcements() })
    },
    onError: (error) => {
      console.error('Announcement highlight failed:', error)
    }
  })
}

